/**
 * mediaPolicy — the single owner of the capsule feed's video delivery
 * policy: which media role each pager page plays (active / next / previous /
 * probe) and how aggressively that role may load, given the network profile
 * inferred from browser hints (effectiveType / downlink / saveData).
 *
 * Pure module (mirrors pagerPolicy): no DOM / network / React imports, so it
 * is unit-testable in isolation and shared by the shell (role + load intent
 * per page) and the regression tests.
 *
 * Delivery reality this policy is tuned for (verified against the PDS, see
 * MEDIA_DELIVERY.md): capsule videos are SINGLE-RENDITION HLS VOD — there is
 * no ABR ladder to switch between, so "adaptive quality" cannot mean bitrate
 * switching here. The real levers are WHEN speculative segments load, HOW
 * MUCH speculative data is fetched, and WHO wins when bandwidth is scarce.
 * Those levers are expressed as role × profile load intents below.
 */

import type {NetworkHints, NetworkProfile} from '#/lib/networkProfile'
export type {NetworkHints, NetworkProfile}
export {networkProfileFromHints} from '#/lib/networkProfile'

/** A page's delivery role while the feed sits at `currentIndex`. */
export type MediaRole = 'active' | 'next' | 'previous' | 'probe'

/**
 * How much of a page's HLS source it may load while it is NOT the active
 * page. The active page is always 'full'.
 */
export type LoadIntent =
  /** Load the whole clip (the active page; promotion to active). */
  | 'full'
  /**
   * Fetch the manifest and the FIRST segment only, then idle. That segment
   * (~8s of video) is the entire startup requirement: a swipe onto this page
   * begins playback from already-buffered data while the rest streams in.
   */
  | 'first-segment'
  /**
   * Fetch only the tiny manifest (cheap warm + duration known), no segments.
   * Used for next/previous on weaker links where even one ~5.6 MB segment of
   * speculation could compete with the active video.
   */
  | 'playlist-only'
  /** No network work at all — the poster carries the page until activation. */
  | 'none'

/** The N+2 probe tier exists only on fast links (bounded to one segment). */
export function probeAllowed(profile: NetworkProfile): boolean {
  return profile === 'fast'
}

/**
 * The load intent for a page in a given role, on a given network profile.
 *
 *  - active always loads in full.
 *  - next / previous: on fast links fetch the startup segment (previous only
 *    actually downloads when it has no buffered data — the loader stops once
 *    the first segment is present); on weaker links keep it to the manifest
 *    so speculation never competes with the active video.
 *  - probe: the manifest + first segment on fast links, nothing otherwise
 *    (the shell only mounts a probe page when probeAllowed() is true).
 */
export function roleLoadIntent(
  role: MediaRole,
  profile: NetworkProfile,
): LoadIntent {
  if (role === 'active') return 'full'
  if (role === 'probe') return profile === 'fast' ? 'first-segment' : 'none'
  if (profile === 'fast') return 'first-segment'
  // medium / poor / save-data: never speculate a multi-MB segment.
  return 'playlist-only'
}
